import React, { useCallback, useState } from 'react';

import _ from 'lodash';
import moment from 'moment';
import { Button, IconButton } from '@material-ui/core';
import { MdDelete, MdEdit, MdFileCopy } from 'react-icons/md';

import API from 'utils/api';
import { useSingleton, useSources, useTableStateRestore } from 'hooks';
import { ButtonContainer, ConfirmDialog, Table, Tooltip } from 'components';
import { breakSentence, call } from 'utils';
import ResponseDialog from './ResponseDialog';

import styles from './MessageFlowList.module.scss';

const MessageFlowList = props => {
  const [dialog, setDialog] = useState({});
  const sources = useSources();
  const singleton = useSingleton();

  const { tableProps, forwardLink } = useTableStateRestore(props);

  const fetchData = useCallback(
    ({ filter, ...rest }) =>
      API.getMessageFlows(
        {
          ...rest,
          query: filter
            ? JSON.stringify(
                _.mapValues(filter, (value, key) =>
                  key === 'name' ? { $regex: value, $options: 'i' } : value
                )
              )
            : undefined,
          select: 'name updatedAt active',
          checkDependents: true,
        },
        sources
      ),
    [sources]
  );

  const renderActions = ({ _id: id, name, hasDependents }) => {
    const button = (Icon, onClick, title, disabled) => (
      <IconButton color="primary" onClick={onClick} disabled={disabled}>
        <Tooltip title={title} placement="top">
          <Icon />
        </Tooltip>
      </IconButton>
    );

    return (
      <div>
        {button(
          MdEdit,
          () => props.history.push(forwardLink(`editor/${id}`)),
          'Edit this flow'
        )}

        {button(
          MdFileCopy,
          () =>
            setDialog({
              clone: {
                id,
                message: [
                  'Are you sure you want to clone the following flow?',
                  `"${name}"`,
                ],
              },
            }),

          'Clone this flow'
        )}

        {button(
          MdDelete,
          () =>
            setDialog({
              delete: {
                id,
                message: [
                  'Are you sure you want to delete the following flow?',
                  `"${name}"`,
                ],
              },
            }),
          'Delete this flow',
          hasDependents
        )}
      </div>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <div className={styles.flowList}>
      <ButtonContainer position="top">
        <Button
          variant="contained"
          color="primary"
          onClick={() => props.history.push(forwardLink('editor'))}
        >
          create new flow
        </Button>
      </ButtonContainer>

      <Table
        {...tableProps}
        data={fetchData}
        title="Message Flow List"
        filtering
        pageSizeOptions={[5, 10, 20, 40]}
        pageSize={10}
        search={false}
        columns={[
          {
            title: 'Name',
            field: 'name',
            weight: 2,
          },
          {
            title: 'Last Modified',
            field: 'updatedAt',
            align: 'center',
            defaultSort: 'desc',
            initialSort: 'desc',
            filtering: false,
            render: ({ updatedAt }) => (
              <div>{moment(updatedAt).format('YYYY/MM/DD hh:mm A')}</div>
            ),
          },
          {
            title: 'Active',
            field: 'active',
            align: 'center',
            weight: 0.5,
            render: ({ active }) => (active ? 'Yes' : 'No'),
            filtering: {
              type: 'select',
              options: [
                { text: 'Yes', value: true },
                { text: 'No', value: false },
              ],
            },
          },
          {
            title: 'Actions',
            sorting: false,
            align: 'center',
            render: renderActions,
            filtering: false,
          },
        ]}
      />

      <ConfirmDialog
        open={!!dialog.delete}
        title="Delete Message Flow"
        message={_.get(dialog, 'delete.message', '')}
        busy={_.get(dialog, 'delete.busy')}
        actions={_.get(dialog, 'delete.actions')}
        onClose={yes => {
          if (yes) {
            const data = dialog.delete;
            setDialog({ delete: { ...data, message: 'Deleting flow...', busy: true } });

            singleton(() =>
              API.deleteFlow(data.id, sources).then(({ error }) => {
                if (error) {
                  setDialog({
                    delete: {
                      ...data,
                      message: breakSentence(String(error), 51),
                      actions: 'close',
                    },
                  });
                } else {
                  call(tableProps.tableRef.current.reload);
                  onCloseDialog();
                }
              })
            );
          } else {
            onCloseDialog();
          }
        }}
      />

      <ConfirmDialog
        open={!!dialog.clone}
        title="Clone Message Flow"
        message={_.get(dialog, 'clone.message', '')}
        busy={_.get(dialog, 'clone.busy')}
        onClose={yes => {
          if (yes) {
            setDialog({
              clone: {
                ...dialog.clone,
                message: 'Cloning flow resources...',
                busy: true,
              },
            });

            singleton(() =>
              API.copyFlow(dialog.clone.id, sources).then(response => {
                if (response.data && response.data.status === 'success') {
                  props.history.push(forwardLink(`editor/${response.data.id}`));
                } else {
                  setDialog({ cloneResponse: response });
                }
              })
            );
          } else {
            onCloseDialog();
          }
        }}
      />

      <ResponseDialog
        response={dialog.cloneResponse}
        onClose={success =>
          success
            ? props.history.push(forwardLink(`editor/${dialog.cloneResponse.data.id}`))
            : onCloseDialog()
        }
      />
    </div>
  );
};

export default MessageFlowList;
